/**
 * Wireless loss versus congestion.
 *
 * Missing packets on a round trip that stays near its floor are the radio dropping
 * datagrams. Missing packets while the round trip climbs are a queue. The first must
 * not be treated as a capacity failure: cutting bitrate does not put the packets back.
 */

import { DEFAULT_CONGESTION_THRESHOLDS } from './streamCongestionSignal';

export type LinkImpairment = 'clear' | 'wireless-loss' | 'congestion';

/**
 * Before a trusted floor exists, absolute round trips through this boundary are
 * still short enough to classify loss as wireless.
 */
export const WIRELESS_RTT_BOUNDARY_MS = 60;
/** Standing queue (round trip above the peer's floor) still considered near-floor. */
export const WIRELESS_LOSS_QUEUE_HEADROOM_MS = 40;

export type LinkImpairmentSample = {
  /** Loss over the sample, percent. Absent is not loss. */
  packetsLostPct?: number;
  /**
   * Excess round trip above this peer's floor, ms. Null or absent means the floor is
   * untrusted. Loss is then congestion only when the absolute round trip is also unknown
   * or already larger than the wireless headroom.
   */
  standingQueueMs?: number | null;
  /**
   * Absolute round trip, ms. When the floor is untrusted this is an upper bound on the
   * queue: the floor cannot be negative, so the excess cannot exceed the whole trip.
   */
  rttMs?: number | null;
  /** Fresh sender throughput over this sample. Missing remains unknown; zero is measured zero. */
  observedSendBitrateBps?: number;
  /** Fresh selected-pair capacity estimate. */
  availableOutgoingBitrateBps?: number;
};

/**
 * Queue figure for the wireless-loss decision.
 *
 * A measured excess wins. Before the floor exists, a short absolute round trip is still
 * decisive: a 7 ms trip cannot be hiding a bottleneck queue.
 */
export function queueForLinkImpairment(sample: LinkImpairmentSample): number | null | undefined {
  const queue = sample.standingQueueMs;
  if (typeof queue === 'number' && Number.isFinite(queue)) return queue;
  const rtt = sample.rttMs;
  if (
    typeof rtt === 'number' &&
    Number.isFinite(rtt) &&
    rtt >= 0 &&
    rtt <= WIRELESS_RTT_BOUNDARY_MS
  ) {
    return rtt;
  }
  return queue;
}

/**
 * Classify loss at or above the elevated threshold (2%) as wireless when measured
 * queue excess is at most 40 ms, or absolute RTT is 0–60 ms when excess is unavailable.
 * Other elevated loss is congestion; absent, nonfinite, or lower loss is clear,
 * regardless of queue size. Loss uses percent and timing uses milliseconds.
 */
export function classifyLinkImpairment(sample: LinkImpairmentSample): LinkImpairment {
  const loss = sample.packetsLostPct;
  if (typeof loss !== 'number' || !Number.isFinite(loss) || loss < DEFAULT_CONGESTION_THRESHOLDS.lossElevatedPct) {
    return 'clear';
  }
  const observed = sample.observedSendBitrateBps;
  const available = sample.availableOutgoingBitrateBps;
  if (typeof observed === 'number' && Number.isFinite(observed) && observed > 0
    && typeof available === 'number' && Number.isFinite(available) && available >= 0
    && available < observed * 0.9) {
    return 'congestion';
  }
  const measuredQueue = sample.standingQueueMs;
  if (typeof measuredQueue === 'number' && Number.isFinite(measuredQueue)
    && measuredQueue <= WIRELESS_LOSS_QUEUE_HEADROOM_MS) {
    return 'wireless-loss';
  }
  const queue = queueForLinkImpairment(sample);
  if ((measuredQueue === null || measuredQueue === undefined)
    && typeof queue === 'number' && Number.isFinite(queue)
    && queue <= WIRELESS_RTT_BOUNDARY_MS) {
    return 'wireless-loss';
  }
  return 'congestion';
}

export type WirelessLossPhase = 'started' | 'ongoing' | 'ended' | 'clear';

/**
 * One wireless-loss burst. `ended` is reported once, on the first clear sample after
 * the burst, so a caller can request a single keyframe.
 */
export class WirelessLossBurst {
  private active = false;
  private droppedFrames = 0;

  /**
   * Advance the burst and accumulate positive dropped-frame counts from wireless
   * samples and the clear sample that ends it. Pass counts per sample, not totals.
   * Congestion returns clear without ending an active burst or adding its drops.
   */
  public observe(impairment: LinkImpairment, framesDropped = 0): WirelessLossPhase {
    if (impairment === 'wireless-loss') {
      this.addDroppedFrames(framesDropped);
      if (this.active) return 'ongoing';
      this.active = true;
      return 'started';
    }
    if (this.active && impairment === 'clear') {
      this.addDroppedFrames(framesDropped);
      this.active = false;
      return 'ended';
    }
    return 'clear';
  }

  /** Frames the guest dropped across the burst that just ended. Zero after it is read. */
  public takeDroppedFrames(): number {
    const dropped = this.droppedFrames;
    this.droppedFrames = 0;
    return dropped;
  }

  private addDroppedFrames(framesDropped: number): void {
    if (framesDropped > 0) this.droppedFrames += framesDropped;
  }
}
